import { useCallback, useMemo } from 'react';
import {
  CompoundEntityRef,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import Box from '@material-ui/core/Box';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { EntityRefOption } from '../../types';

const useStyles = makeStyles({
  formControl: {
    maxWidth: 300,
  },
});

export const EntityFilter = (props: {
  value: CompoundEntityRef[];
  options: EntityRefOption[];
  onChange: (value: CompoundEntityRef[]) => void;
}) => {
  const { value, options, onChange } = props;
  const stringifiedValue = value?.map(v => stringifyEntityRef(v));
  const stringifiedOptions = options.map(o => stringifyEntityRef(o));
  const classes = useStyles();
  const handleChange = useCallback(
    (_: unknown, v: string[]) => {
      const results = stringifiedOptions.every(o => v.includes(o))
        ? stringifiedOptions
        : v;
      onChange(results.map(r => parseEntityRef(r)));
    },
    [stringifiedOptions, onChange],
  );
  const lookup = useMemo(() => {
    const record: Record<string, string> = {};
    for (const o of options) {
      record[stringifyEntityRef(o).toLocaleLowerCase('en-US')] = o.label;
    }
    return record;
  }, [options]);
  return (
    <Box pb={1} pt={1}>
      <Typography variant="button">Filter by Root Entities</Typography>
      <Autocomplete
        className={classes.formControl}
        multiple
        limitTags={10}
        disableCloseOnSelect
        options={stringifiedOptions}
        value={stringifiedValue}
        getOptionLabel={o => (lookup[o] ? lookup[o] : o)}
        onChange={handleChange}
        renderOption={(o, { selected }) => (
          <FormControlLabel
            control={
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                checked={selected}
              />
            }
            label={lookup[o] ? lookup[o] : o}
          />
        )}
        size="small"
        popupIcon={<ExpandMoreIcon />}
        renderInput={params => <TextField {...params} variant="outlined" />}
      />
    </Box>
  );
};
